import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { pushFormEvent } from "@/lib/analytics";
import { AUTH } from "@/constants/testIds";
import { BrandLogo } from "@/components/BrandLogo";

export default function CustomerRegister() {
  const [form, setForm] = useState({ email: "", password: "", full_name: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [providers, setProviders] = useState({ google: false, facebook: false });
  const { refresh } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/providers").then(({ data }) => setProviders(data)).catch(() => {});
  }, []);

  // Same OAuth entry as /login — the backend decides sign-in vs signup:
  // linked identity → login; email match → link to existing account
  // (role preserved); brand-new → Customer account created.
  const startSocial = (provider) => {
    window.location.assign(`${process.env.REACT_APP_BACKEND_URL}/api/auth/social/${provider}/start`);
  };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/customer/register", form);
      localStorage.setItem("scwt_token", data.token);
      await refresh();
      toast.success("Account created! Welcome to Aware Water Testing.");
      pushFormEvent("registration_form_submit", {
        form_name: "customer_register",
        form_type: "registration",
        registration_type: "customer",
      });
      navigate("/customer");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <BrandLogo to="/" size="sm" />
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-600">Customer Account</div>
            <h1 className="font-display mt-2 text-3xl lg:text-4xl font-bold text-slate-900">Create your account</h1>
            <p className="mt-2 text-slate-600">Track all your past and future water tests in one place. Instant access — no waiting for approval.</p>
          </div>
          {(providers.google || providers.facebook) && (
            <div className="mb-5">
              <div className="space-y-3">
                {providers.google && (
                  <button data-testid="register-google-btn" type="button" onClick={() => startSocial("google")}
                    className="w-full h-12 flex items-center justify-center gap-3 rounded-full border border-slate-300 bg-white font-semibold text-slate-800 hover:bg-slate-50 transition-colors">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
                    Continue with Google
                  </button>
                )}
                {providers.facebook && (
                  <button data-testid="register-facebook-btn" type="button" onClick={() => startSocial("facebook")}
                    className="w-full h-12 flex items-center justify-center gap-3 rounded-full border border-slate-300 bg-white font-semibold text-slate-800 hover:bg-slate-50 transition-colors">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true"><path fill="#1877F2" d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07c0 6.02 4.39 11.02 10.13 11.93v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.7 4.53-4.7 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.95.93-1.95 1.89v2.26h3.32l-.53 3.49h-2.79V24C19.61 23.09 24 18.09 24 12.07z"/></svg>
                    Continue with Facebook
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3 pt-4">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-[11px] uppercase tracking-wider font-bold text-slate-400">or</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>
            </div>
          )}
          <form onSubmit={submit} className="bg-white rounded-3xl border border-slate-200 p-7 shadow-sm space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Full Name</Label>
              <Input data-testid={AUTH.registerFullName} required value={form.full_name} onChange={update("full_name")} className="mt-1.5 h-12 rounded-xl" placeholder="Jane Doe" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Email</Label>
              <Input data-testid={AUTH.registerEmail} type="email" required value={form.email} onChange={update("email")} className="mt-1.5 h-12 rounded-xl" placeholder="you@example.com" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Phone <span className="normal-case text-slate-400">(optional)</span></Label>
              <Input data-testid={AUTH.registerPhone} value={form.phone} onChange={update("phone")} className="mt-1.5 h-12 rounded-xl" placeholder="04XX XXX XXX" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Password</Label>
              <Input data-testid={AUTH.registerPassword} type="password" required minLength={8} value={form.password} onChange={update("password")} className="mt-1.5 h-12 rounded-xl" placeholder="At least 8 characters" />
            </div>
            <Button data-testid={AUTH.registerSubmit} disabled={submitting} type="submit" size="lg" className="w-full btn-aqua rounded-full font-semibold mt-2">
              {submitting ? "Creating..." : <>Create account <ArrowRight className="w-4 h-4 ml-1.5" /></>}
            </Button>
          </form>
          <p className="mt-6 text-center text-sm text-slate-500">
            Already have an account? <Link to="/customer/login" className="font-semibold text-cyan-600 hover:underline">Sign in</Link>
          </p>
        </div>
      </main>
    </div>
  );
}
